import React, { useState, useEffect, useRef } from 'react';
import { EditableTemplateContent } from './EditableTemplateContent';

interface PagedDocumentViewProps {
  content: string;
  onContentChange?: (content: string) => void;
  onPlaceholderChange?: (placeholder: string, value: string) => void;
  placeholderValues?: Record<string, string>;
  onPlaceholdersUpdate?: (placeholders: string[], displayNames: Record<string, string>) => void;
}

interface Page {
  id: string;
  content: string;
  pageNumber: number;
}

export const PagedDocumentView = React.memo(({
  content,
  onContentChange,
  onPlaceholderChange,
  placeholderValues = {},
  onPlaceholdersUpdate
}: PagedDocumentViewProps) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [isInternalChange, setIsInternalChange] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // A4 dimensions for reference (currently using CSS-based sizing)
  // These could be used for future height-based page breaking
  // const PAGE_HEIGHT = 842; // 29.7cm at 96 DPI
  // const PAGE_WIDTH = 595; // 21cm at 96 DPI
  // const PAGE_MARGIN = 40; // 1cm margin in pixels
  // const CONTENT_HEIGHT = PAGE_HEIGHT - (PAGE_MARGIN * 2);

  // Break content into pages based on height
  const breakContentIntoPages = (htmlContent: string): Page[] => {
    if (!htmlContent) return [];
    
    // Check if content has explicit page breaks
    if (htmlContent.includes('page-break') || htmlContent.includes('page-break-before') || htmlContent.includes('page-break-after')) {
      return breakByExplicitPageBreaks(htmlContent);
    }
    
    // Otherwise, estimate pages based on content length
    return breakByContentLength(htmlContent);
  };

  // Break content by explicit page break classes
  const breakByExplicitPageBreaks = (htmlContent: string): Page[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const contentContainer = doc.querySelector('.template-content') || doc.body;
    
    const pages: Page[] = [];
    let currentPageContent = '';
    let pageNumber = 1;
    let elementCount = 0;
    
    // If no explicit breaks are found but content is long, fall back to length-based breaking
    const hasActualPageBreaks = Array.from(contentContainer.children).some(element =>
      element.classList.contains('page-break') || 
      element.classList.contains('page-break-before') || 
      element.classList.contains('page-break-after')
    );
    
    if (!hasActualPageBreaks) {
      return breakByContentLength(htmlContent);
    }
    
    Array.from(contentContainer.children).forEach((element, index) => {
      // Check for page break before adding element
      if (element.classList.contains('page-break') || element.classList.contains('page-break-before')) {
        if (currentPageContent.trim()) {
          pages.push({
            id: `page-${pageNumber}`,
            content: `<div class="template-content">${currentPageContent}</div>`,
            pageNumber: pageNumber++
          });
          currentPageContent = '';
          elementCount = 0;
        }
      }
      
      // Add element to current page
      currentPageContent += element.outerHTML;
      elementCount++;
      
      // Check for page break after adding element
      if (element.classList.contains('page-break-after')) {
        pages.push({
          id: `page-${pageNumber}`,
          content: `<div class="template-content">${currentPageContent}</div>`,
          pageNumber: pageNumber++
        });
        currentPageContent = '';
        elementCount = 0;
      }
      
      // Also break if page gets too long even with explicit breaks
      else if (elementCount >= 20 && index < contentContainer.children.length - 1) {
        pages.push({
          id: `page-${pageNumber}`,
          content: `<div class="template-content">${currentPageContent}</div>`,
          pageNumber: pageNumber++
        });
        currentPageContent = '';
        elementCount = 0;
      }
    });
    
    // Add remaining content as final page
    if (currentPageContent.trim()) {
      pages.push({
        id: `page-${pageNumber}`,
        content: `<div class="template-content">${currentPageContent}</div>`,
        pageNumber: pageNumber
      });
    }
    
    return pages.length > 0 ? pages : [{ id: 'page-1', content: htmlContent, pageNumber: 1 }];
  };

  // Break content by estimated length (fallback method)
  const breakByContentLength = (htmlContent: string): Page[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const contentContainer = doc.querySelector('.template-content') || doc.body;
    
    const pages: Page[] = [];
    let currentPageContent = '';
    let elementCount = 0;
    let pageNumber = 1;
    
    // Estimate elements per page (this can be adjusted based on content type)
    const ESTIMATED_ELEMENTS_PER_PAGE = 8; // Reduced for more frequent page breaks
    
    Array.from(contentContainer.children).forEach((element, index) => {
      // Add element to current page
      currentPageContent += element.outerHTML;
      elementCount++;
      
      // Check if we should start a new page
      const shouldBreakPage = (
        elementCount >= ESTIMATED_ELEMENTS_PER_PAGE ||
        (element.tagName.toLowerCase() === 'table' && elementCount >= 6) || // Tables take more space
        (element.tagName.toLowerCase().match(/^h[1-3]$/) && elementCount >= 10) // Headers with some content
      );
      
      if (shouldBreakPage && index < contentContainer.children.length - 1) {
        // Create current page
        pages.push({
          id: `page-${pageNumber}`,
          content: `<div class="template-content">${currentPageContent}</div>`,
          pageNumber: pageNumber++
        });
        
        // Reset for next page
        currentPageContent = '';
        elementCount = 0;
      }
    });
    
    // Add remaining content as final page
    if (currentPageContent.trim()) {
      pages.push({
        id: `page-${pageNumber}`,
        content: `<div class="template-content">${currentPageContent}</div>`,
        pageNumber: pageNumber
      });
    }
    
    // If no elements were processed, return the original content as one page
    if (pages.length === 0) {
      pages.push({
        id: 'page-1',
        content: htmlContent,
        pageNumber: 1
      });
    }
    
    return pages;
  };

  // Update pages when content changes (but not from internal changes)
  useEffect(() => {
    if (content && !isInternalChange) {
      const newPages = breakContentIntoPages(content);
      setPages(newPages);
    } else if (isInternalChange) {
      // Reset the flag after handling internal change
      setIsInternalChange(false);
    }
  }, [content, isInternalChange]);

  // Handle content changes from individual pages
  const handlePageContentChange = (pageNumber: number, newContent: string) => {
    const updatedPages = pages.map(page => 
      page.pageNumber === pageNumber 
        ? { ...page, content: newContent }
        : page
    );
    
    // Combine all pages content - extract inner content from template-content divs
    const combinedContent = updatedPages.map(page => {
      // Extract content from template-content wrapper if it exists
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = page.content;
      const templateContent = tempDiv.querySelector('.template-content');
      return templateContent ? templateContent.innerHTML : page.content;
    }).join('\n');
    
    // Wrap in template-content div
    const wrappedContent = `<div class="template-content">${combinedContent}</div>`;
    
    // Re-break the combined content into pages (this handles new sections properly)
    const newPages = breakContentIntoPages(wrappedContent);
    console.log(`PagedDocumentView: Re-broke content into ${newPages.length} pages after content change`);
    setPages(newPages);
    
    // Mark as internal change to prevent re-breaking from useEffect
    setIsInternalChange(true);
    
    onContentChange?.(wrappedContent);
  };

  return (
    <div ref={containerRef} className="paged-document-view">
      <div className="pages-container">
        {pages.map((page, index) => (
          <div key={page.id} className="document-page">
            <div className="page-header">
              <span className="page-number">Page {page.pageNumber}</span>
            </div>
            <div className="page-content">
              <EditableTemplateContent
                content={page.content}
                onContentChange={(newContent) => handlePageContentChange(page.pageNumber, newContent)}
                onPlaceholderChange={onPlaceholderChange}
                placeholderValues={placeholderValues}
                onPlaceholdersUpdate={(placeholders, displayNames) => {
                  // Mark as internal change when placeholders are updated
                  setIsInternalChange(true);
                  onPlaceholdersUpdate?.(placeholders, displayNames);
                }}
              />
            </div>
            {index < pages.length - 1 && <div className="page-separator" />}
          </div>
        ))}
      </div>
    </div>
  );
});

PagedDocumentView.displayName = 'PagedDocumentView';